import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';
import { actionButtonSx, pageTitleSx } from '../styles/uiTokens';

function ImportacaoCadastrosPage() {
  const [arquivo, setArquivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importando, setImportando] = useState(false);
  const [mensagem, setMensagem] = useState(null);

  const baixarGabarito = async () => {
    const response = await api.get('/importacoes/gabarito', { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'gabarito-cadastros.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const validar = async () => {
    if (!arquivo) return;
    setLoading(true);
    setMensagem(null);
    setResultado(null);
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    try {
      const response = await api.post('/importacoes/validar', formData);
      setResultado(response.data);
    } catch (error) {
      setMensagem({ severity: 'error', text: error.response?.data?.message || 'Não foi possível validar o arquivo.' });
    } finally {
      setLoading(false);
    }
  };

  const importar = async () => {
    if (!resultado?.linhas?.length) return;
    setImportando(true);
    setMensagem(null);
    try {
      const response = await api.post('/importacoes/importar', {
        empresa_id: resultado.empresa_id,
        linhas: resultado.linhas,
      });
      setMensagem({ severity: 'success', text: `${response.data.message} Pessoas: ${response.data.criados.pessoas}, imóveis: ${response.data.criados.imoveis}, moradores: ${response.data.criados.moradores}, proprietários: ${response.data.criados.proprietarios}.` });
      setResultado(null);
      setArquivo(null);
    } catch (error) {
      setMensagem({ severity: 'error', text: error.response?.data?.message || 'Não foi possível concluir a importação.' });
    } finally {
      setImportando(false);
    }
  };

  const baixarErros = () => {
    if (!resultado?.erros?.length) return;
    const csv = ['Linha;Erros', ...resultado.erros.map((item) => `${item.linha};"${item.erros.join(' | ').replaceAll('"', '""')}"`)].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'relatorio-erros-importacao.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={pageTitleSx}>Importar cadastros</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>Pessoas, moradores e proprietários em uma única planilha.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={baixarGabarito}>Baixar gabarito</Button>
      </Box>

      {mensagem && <Alert severity={mensagem.severity} sx={{ mb: 2 }}>{mensagem.text}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">1. Selecione o arquivo</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Use o gabarito oficial e mantenha o CPF preenchido em todas as linhas.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            Escolher Excel
            <input hidden type="file" accept=".xlsx,.xls" onChange={(event) => { setArquivo(event.target.files?.[0] || null); setResultado(null); }} />
          </Button>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{arquivo?.name || 'Nenhum arquivo selecionado'}</Typography>
          <Button variant="contained" disabled={!arquivo || loading} onClick={validar} sx={actionButtonSx}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Validar arquivo'}
          </Button>
        </Stack>
      </Paper>

      {resultado && (
        <Paper sx={{ mt: 3, p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">2. Resultado da validação</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${resultado.total} linhas`} />
            <Chip color="success" icon={<CheckCircleIcon />} label={`${resultado.validas} válidas`} />
            <Chip color={resultado.erros.length ? 'error' : 'default'} label={`${resultado.erros.length} com erro`} />
          </Stack>
          <Divider />
          {resultado.erros.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" color="error" sx={{ mb: 1 }}>Linhas rejeitadas</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Linha</TableCell><TableCell>Motivos</TableCell></TableRow></TableHead>
                  <TableBody>{resultado.erros.map((item) => <TableRow key={item.linha}><TableCell>{item.linha}</TableCell><TableCell>{item.erros.join(' ')}</TableCell></TableRow>)}</TableBody>
                </Table>
              </Box>
              <Button sx={{ mt: 1 }} size="small" startIcon={<DownloadIcon />} onClick={baixarErros}>Baixar relatório de erros</Button>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" disabled={!resultado.validas || importando} onClick={importar} sx={actionButtonSx}>
              {importando ? <CircularProgress size={22} color="inherit" /> : `Importar ${resultado.validas} linha(s) válida(s)`}
            </Button>
            <Button variant="outlined" onClick={() => setResultado(null)}>Cancelar</Button>
          </Stack>
        </Paper>
      )}
    </Container>
  );
}

export default ImportacaoCadastrosPage;
